import api from './api';
import { Promotion } from './promotionApi';

export interface PromoWheelConfig {
  id: number;
  replay_delay_days: number;
  win_delay_days: number;
  is_active: boolean;
  updated_at: string;
  updated_by?: number;
  updated_by_name?: string;
}

export interface PromoWheelItem {
  id: number;
  promotion?: number;
  promotion_data?: Promotion;
  is_no_win: boolean;
  probability_weight: number;
  is_guaranteed_win: boolean;
  is_first_time_prize: boolean;
  order: number;
  is_active: boolean;
  display_label: string;
  created_at: string;
  updated_at: string;
}

export interface WheelSpin {
  id: number;
  user: number;
  user_name: string;
  user_email: string;
  wheel_item?: number;
  wheel_item_label?: string;
  promotion_won?: number;
  promotion_name?: string;
  is_first_spin: boolean;
  has_won: boolean;
  spin_date: string;
  ip_address?: string;
}

export interface WheelData {
  active: boolean;
  items: Array<{
    id: number;
    label: string;
    is_no_win: boolean;
    promotion_id?: number;
    promotion_data?: Promotion;
  }>;
  config: {
    replay_delay_days: number;
    win_delay_days: number;
    can_play: boolean;
    can_win: boolean;
    last_spin_date?: string;
    last_win_date?: string;
    days_until_next_play: number;
    days_until_next_win: number;
  };
  message?: string;
}

export interface SpinResult {
  spin_id: number;
  has_won: boolean;
  is_first_spin: boolean;
  winning_item: {
    id: number;
    label: string;
    is_no_win: boolean;
  };
  promotion?: Promotion;
  promo_code?: string;
}

export interface WheelStats {
  total_spins: number;
  total_wins: number;
  total_losses: number;
  total_first_spins: number;
  total_users: number;
  win_rate: number;
  popular_wins: {
    [key: string]: number;
  };
}

export interface WheelUser {
  id: number;
  email: string;
  full_name: string;
  is_wheel_blocked: boolean;
  total_spins: number;
  total_wins: number;
  last_spin_date: string | null;
  all_spins: Array<{
    id: number;
    spin_date: string;
    has_won: boolean;
    winning_item_label: string;
    promotion_name: string | null;
  }>;
}

export interface CreateWheelItemData {
  promotion?: number;
  is_no_win?: boolean;
  probability_weight: number;
  is_guaranteed_win?: boolean;
  is_first_time_prize?: boolean;
  order?: number;
  is_active?: boolean;
}

export interface UpdateWheelItemData extends Partial<CreateWheelItemData> {}

class WheelApiService {
  /**
   * Récupère la configuration de la roue
   */
  async getConfig(): Promise<PromoWheelConfig> {
    try {
      const response = await api.get('/api/promo-wheel/config/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération de la config:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération de la configuration');
    }
  }

  /**
   * Met à jour la configuration de la roue
   */
  async updateConfig(data: Partial<PromoWheelConfig>): Promise<PromoWheelConfig> {
    try {
      const response = await api.patch('/api/promo-wheel/config/', data);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour de la config:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la mise à jour de la configuration');
    }
  }

  /**
   * Récupère tous les items de la roue
   */
  async getItems(isActive?: boolean): Promise<PromoWheelItem[]> {
    try {
      const params: any = {};
      if (isActive !== undefined) params.is_active = isActive;
      
      const response = await api.get('/api/promo-wheel/items/', { params });
      
      // Django REST Framework retourne un objet de pagination avec "results"
      const items = Array.isArray(response.data) 
        ? response.data 
        : (response.data.results || []);
      
      console.log('✅ Items chargés:', items.length);
      return items;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des items:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des items');
    }
  }

  /**
   * Crée un item dans la roue
   */
  async createItem(data: CreateWheelItemData): Promise<PromoWheelItem> {
    try {
      const response = await api.post('/api/promo-wheel/items/', data);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création de l\'item:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data) {
        const errors = Object.entries(error.response.data)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        throw new Error(errors);
      }
      throw new Error('Erreur lors de la création de l\'item');
    }
  }

  /**
   * Met à jour un item
   */
  async updateItem(id: number, data: UpdateWheelItemData): Promise<PromoWheelItem> {
    try {
      const response = await api.patch(`/api/promo-wheel/items/${id}/`, data);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour de l\'item:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la mise à jour de l\'item');
    }
  }

  /**
   * Supprime un item
   */
  async deleteItem(id: number): Promise<void> {
    try {
      await api.delete(`/api/promo-wheel/items/${id}/`);
    } catch (error: any) {
      console.error('Erreur lors de la suppression de l\'item:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la suppression de l\'item');
    }
  }

  /**
   * Récupère les données de la roue pour l'affichage
   */
  async getWheelData(): Promise<WheelData> {
    try {
      const response = await api.get('/api/promo-wheel/data/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des données de la roue:', error);
      throw new Error('Erreur lors de la récupération des données');
    }
  }

  /**
   * Fait tourner la roue
   */
  async spin(): Promise<SpinResult> {
    try {
      const response = await api.post('/api/promo-wheel/spin/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors du tour de roue:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors du tour de roue');
    }
  }

  /**
   * Récupère l'historique des tours
   */
  async getHistory(): Promise<WheelSpin[]> {
    try {
      const response = await api.get('/api/promo-wheel/history/');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      throw new Error('Erreur lors de la récupération de l\'historique');
    }
  }

  /**
   * Récupère les statistiques de la roue
   */
  async getStats(): Promise<WheelStats> {
    try {
      const response = await api.get('/api/promo-wheel/stats/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des stats:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des statistiques');
    }
  }

  /**
   * Récupère la liste des utilisateurs qui ont joué à la roue
   */
  async getWheelUsers(): Promise<WheelUser[]> {
    try {
      const response = await api.get('/api/promo-wheel/users/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des utilisateurs');
    }
  }

  /**
   * Bloque ou débloque un utilisateur sur la roue
   */
  async toggleUserWheelBlock(userId: number): Promise<{ success: boolean; message: string; is_wheel_blocked: boolean }> {
    try {
      const response = await api.post(`/api/promo-wheel/users/${userId}/toggle-block/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors du blocage/déblocage:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors du blocage/déblocage de l\'utilisateur');
    }
  }

  /**
   * Valide les données d'un item
   */
  validateItemData(data: CreateWheelItemData | UpdateWheelItemData): string[] {
    const errors: string[] = [];

    if (!data.promotion) {
      errors.push('Une promotion doit être associée');
    }

    if ('probability_weight' in data && (data.probability_weight === undefined || data.probability_weight < 1)) {
      errors.push('Le poids de probabilité doit être supérieur ou égal à 1');
    }

    return errors;
  }
}

export const wheelApiService = new WheelApiService();
